"use client";

import { useCallback } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "@/shared/feedback/toast";
import {
  type PersonalData,
  type UpdatePersonalDataRequest,
} from "@/generated/fastapi";
import { useUserProfile } from "@/state/globalStore";
import useResetPersonalDataMutation from "../mutations/useResetPersonalDataMutation";
import useUpdatePersonalDataMutation from "../mutations/useUpdatePersonalDataMutation";

const TOAST_SUCCESS_TIMEOUT = 800;

export default function usePersonalDataActions() {
  const { userProfile, updateUserProfilePersonalData } = useUserProfile();
  const updateMutation = useUpdatePersonalDataMutation();
  const resetMutation = useResetPersonalDataMutation();

  const updatePersonalDataSync = useCallback(
    async (updates: Partial<PersonalData>): Promise<PersonalData | null> => {
      if (!updates || Object.keys(updates).length === 0) return null;

      const currentData = userProfile.data.personal_data;
      const backup = updateUserProfilePersonalData(currentData);

      try {
        const request: UpdatePersonalDataRequest = {
          schema_version: currentData.schema_version,
          allow_print: updates.allow_print ?? currentData.allow_print,
          first_name: updates.first_name ?? currentData.first_name,
          last_name: updates.last_name ?? currentData.last_name,
          email: updates.email ?? currentData.email,
          phone: updates.phone ?? currentData.phone,
          address: updates.address ?? currentData.address,
          linkedin_url: updates.linkedin_url ?? currentData.linkedin_url,
          github_url: updates.github_url ?? currentData.github_url,
          claim: updates.claim ?? currentData.claim,
          intro: updates.intro ?? currentData.intro,
        };

        const result = await updateMutation.mutateAsync(request);
        updateUserProfilePersonalData(result);
        showSuccessToast(
          "Personal data updated successfully",
          TOAST_SUCCESS_TIMEOUT,
        );
        return result;
      } catch (err) {
        updateUserProfilePersonalData(backup);
        showErrorToast(
          err instanceof Error && err.message
            ? err.message
            : "Failed to update personal data",
        );
        return null;
      }
    },
    [updateMutation, updateUserProfilePersonalData, userProfile.data.personal_data],
  );

  const resetPersonalDataSync = useCallback(async (): Promise<PersonalData | null> => {
    const backup = updateUserProfilePersonalData(userProfile.data.personal_data);

    try {
      const response = await resetMutation.mutateAsync();
      if (!response.reset_data) {
        throw new Error("Missing reset data");
      }

      updateUserProfilePersonalData(response.reset_data);
      showSuccessToast(
        "Personal data reset to defaults",
        TOAST_SUCCESS_TIMEOUT,
      );
      return response.reset_data;
    } catch {
      updateUserProfilePersonalData(backup);
      showErrorToast("Failed to reset personal data");
      return null;
    }
  }, [resetMutation, updateUserProfilePersonalData, userProfile.data.personal_data]);

  return {
    updatePersonalDataSync,
    resetPersonalDataSync,
    isUpdating: updateMutation.isPending,
    isResetting: resetMutation.isPending,
  };
}
